import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["__scopeDialog", "trapFocus", "onOpenAutoFocus", "onCloseAutoFocus"],
  _excluded2 = ["forceMount"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { DismissableLayer } from '../../internal/components/DismissableLayer';
import { FocusScope } from '../../internal/components/FocusScope';
import { Presence } from '../../internal/components/Presence';
import { useComposedRefs } from '../../internal/hooks/useComposedRefs';
import { useDisablePageScroll } from '../../internal/hooks/useDisablePageScroll';
import { useFocusGuards } from '../../internal/hooks/useFocusGuards';
import { composeEventHandlers } from '../../internal/utils/composeEventHandlers';
import { CONTENT_NAME } from './constants';
import { useDialogContext, usePortalContext } from './context';
import { getDataStateValue } from './utils';
import { useCallbackRef } from '../../internal/hooks/useCallbackRef';
import { jsx as _jsx } from "react/jsx-runtime";
var DialogContentImpl = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var __scopeDialog = props.__scopeDialog,
    trapFocus = props.trapFocus,
    onOpenAutoFocus = props.onOpenAutoFocus,
    onCloseAutoFocus = props.onCloseAutoFocus,
    contentProps = _objectWithoutProperties(props, _excluded);
  var context = useDialogContext(CONTENT_NAME, __scopeDialog);
  var contentRef = React.useRef(null);
  var composedRefs = useComposedRefs(forwardedRef, contentRef);

  // Убедитесь, что во всем дереве есть focus-guards, так как наш `Dialog` будет
  // последним элементом в DOM (из-за `Portal`)
  useFocusGuards();
  return /*#__PURE__*/_jsx(FocusScope, {
    asChild: true,
    loop: true,
    trapped: trapFocus,
    onMountAutoFocus: onOpenAutoFocus,
    onUnmountAutoFocus: onCloseAutoFocus,
    children: /*#__PURE__*/_jsx(DismissableLayer, _objectSpread(_objectSpread({
      role: "dialog",
      id: context.contentId,
      "aria-describedby": context.descriptionId,
      "aria-labelledby": context.titleId,
      "data-state": getDataStateValue(context.open)
    }, contentProps), {}, {
      ref: composedRefs,
      onDismiss: function onDismiss() {
        return context.onOpenChange(false);
      }
    }))
  });
});
DialogContentImpl.displayName = "".concat(CONTENT_NAME, ".Impl");

/* -----------------------------------------------------------------------------------------------*/

var DialogContentModal = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var context = useDialogContext(CONTENT_NAME, props.__scopeDialog);
  var contentRef = React.useRef(null);
  var composedRefs = useComposedRefs(forwardedRef, context.contentRef, contentRef);
  var hadleOnPointerDownOutsude = React.useMemo(function () {
    return composeEventHandlers(props.onPointerDownOutside, function (event) {
      var originalEvent = event.detail.originalEvent;
      var ctrlLeftClick = originalEvent.button === 0 && originalEvent.ctrlKey === true;
      var isRightClick = originalEvent.button === 2 || ctrlLeftClick;

      // Если событие является правым щелчком, мы не должны закрывать, потому что
      // это фактически как если бы мы щелкнули правой кнопкой мыши по `Overlay`.
      if (isRightClick) {
        event.preventDefault();
      }
    });
  }, [props.onPointerDownOutside]);
  useDisablePageScroll(true);
  return /*#__PURE__*/_jsx(DialogContentImpl, _objectSpread(_objectSpread({}, props), {}, {
    ref: composedRefs
    // мы убеждаемся, что фокус не захватывается, когда `DialogContent` закрыт
    // (закрыт !== размонтирован при анимации)
    ,
    trapFocus: context.open,
    disableOutsidePointerEvents: true,
    onCloseAutoFocus: composeEventHandlers(props.onCloseAutoFocus, function (event) {
      var _context$triggerRef$c;
      event.preventDefault();
      (_context$triggerRef$c = context.triggerRef.current) === null || _context$triggerRef$c === void 0 || _context$triggerRef$c.focus();
    }),
    onPointerDownOutside: hadleOnPointerDownOutsude
    // Когда фокус захвачен, событие `focusout` все равно может произойти.
    // Мы убеждаемся, что не вызываем наш `onDismiss` в таком случае.
    ,
    onFocusOutside: composeEventHandlers(props.onFocusOutside, function (event) {
      return event.preventDefault();
    })
  }));
});
DialogContentModal.displayName = "".concat(CONTENT_NAME, ".Modal");

/* -----------------------------------------------------------------------------------------------*/

var DialogContentNonModal = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var context = useDialogContext(CONTENT_NAME, props.__scopeDialog);
  var hasInteractedOutsideRef = React.useRef(false);
  var hasPointerDownOutsideRef = React.useRef(false);
  var handleOnCloseAutoFocus = useCallbackRef(function (event) {
    var _props$onCloseAutoFoc;
    (_props$onCloseAutoFoc = props.onCloseAutoFocus) === null || _props$onCloseAutoFoc === void 0 || _props$onCloseAutoFoc.call(props, event);
    if (!event.defaultPrevented) {
      if (!hasInteractedOutsideRef.current) {
        var _context$triggerRef$c2;
        (_context$triggerRef$c2 = context.triggerRef.current) === null || _context$triggerRef$c2 === void 0 || _context$triggerRef$c2.focus();
      }
      // Всегда предотвращаем автоматический фокус, потому что мы либо фокусируем вручную, либо хотим фокус пользователя
      event.preventDefault();
    }
    hasInteractedOutsideRef.current = false;
    hasPointerDownOutsideRef.current = false;
  });
  var handleOnInteractOutside = useCallbackRef(function (event) {
    var _props$onInteractOuts, _context$triggerRef$c3;
    (_props$onInteractOuts = props.onInteractOutside) === null || _props$onInteractOuts === void 0 || _props$onInteractOuts.call(props, event);
    if (!event.defaultPrevented) {
      hasInteractedOutsideRef.current = true;
      if (event.detail.originalEvent.type === 'pointerdown') {
        hasPointerDownOutsideRef.current = true;
      }
    }

    // Предотвращаем закрытие при нажатии на триггер.
    // Поскольку триггер уже настроен на закрытие, без этого он закроется и сразу же откроется.
    var target = event.target;
    var targetIsTrigger = (_context$triggerRef$c3 = context.triggerRef.current) === null || _context$triggerRef$c3 === void 0 ? void 0 : _context$triggerRef$c3.contains(target);
    if (targetIsTrigger) {
      event.preventDefault();
    }

    // В Safari, если триггер находится внутри контейнера с tabIndex={0}, при нажатии
    // мы получим событие pointer down outside на триггере, но затем последующее
    // событие focus outside на контейнере, мы игнорируем любое событие focus outside, когда у нас уже было событие pointer down outside.
    if (event.detail.originalEvent.type === 'focusin' && hasPointerDownOutsideRef.current) {
      event.preventDefault();
    }
  });
  return /*#__PURE__*/_jsx(DialogContentImpl, _objectSpread(_objectSpread({}, props), {}, {
    ref: forwardedRef,
    trapFocus: false,
    disableOutsidePointerEvents: false,
    onCloseAutoFocus: handleOnCloseAutoFocus,
    onInteractOutside: handleOnInteractOutside
  }));
});
DialogContentNonModal.displayName = "".concat(CONTENT_NAME, ".NonModal");

/* -----------------------------------------------------------------------------------------------*/

export var DialogContent = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var portalContext = usePortalContext(CONTENT_NAME, props.__scopeDialog);
  var _props$forceMount = props.forceMount,
    forceMount = _props$forceMount === void 0 ? portalContext.forceMount : _props$forceMount,
    contentProps = _objectWithoutProperties(props, _excluded2);
  var context = useDialogContext(CONTENT_NAME, props.__scopeDialog);
  return /*#__PURE__*/_jsx(Presence, {
    present: forceMount || context.open,
    children: context.modal ? /*#__PURE__*/_jsx(DialogContentModal, _objectSpread(_objectSpread({}, contentProps), {}, {
      ref: forwardedRef
    })) : /*#__PURE__*/_jsx(DialogContentNonModal, _objectSpread(_objectSpread({}, contentProps), {}, {
      ref: forwardedRef
    }))
  });
});
DialogContent.displayName = CONTENT_NAME;